/**
 * CAND-DISP-1 — Candidate Review data access.
 *
 * Every call is a Supabase Edge Function invocation (server-side authorized:
 * requireAdmin, then an independent admin re-check inside the SECURITY DEFINER
 * RPC). No client writes, so the client-write gate stays green. The candidate
 * tables are RLS-revoked from authenticated, so even the reads route through
 * the function.
 */

import { supabase } from '@/auth/supabase-client';
import {
  ensureFreshSession,
  SUPABASE_EDGE_TIMEOUT_MS,
  withSupabaseTimeout,
} from '@/auth/session-guard';
import type {
  Disposition,
  DispositionResult,
  OrganizationOption,
  PendingCandidate,
} from './types';

const FUNCTION_NAME = 'resolve_candidate_organization';

async function messageFor(error: unknown): Promise<string> {
  const ctx = (error as { context?: Response }).context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const parsed = (await ctx.json()) as {
        error?: { message?: string } | string;
        message?: string;
      };
      if (parsed && typeof parsed.error === 'object' && parsed.error?.message) {
        return parsed.error.message;
      }
      if (parsed && typeof parsed.message === 'string') return parsed.message;
    } catch {
      /* non-JSON error body — fall through */
    }
  }
  return error instanceof Error ? error.message : String(error);
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke(FUNCTION_NAME, { body }),
    `${FUNCTION_NAME} request`,
    SUPABASE_EDGE_TIMEOUT_MS,
  );
  if (error) throw new Error(`${FUNCTION_NAME}: ${await messageFor(error)}`);
  const payload = (data ?? {}) as { data?: T } & T;
  return (payload.data ?? payload) as T;
}

export async function listPendingCandidates(): Promise<PendingCandidate[]> {
  const result = await invoke<{ candidates: PendingCandidate[] }>({
    action: 'list',
  });
  return result.candidates ?? [];
}

export async function searchOrganizations(
  query: string,
): Promise<OrganizationOption[]> {
  const result = await invoke<{ organizations: OrganizationOption[] }>({
    action: 'search_organizations',
    query,
  });
  return result.organizations ?? [];
}

export async function disposeCandidate(input: {
  candidateOrganizationId: string;
  disposition: Disposition;
  canonicalOrganizationId?: string | null;
  reviewNotes?: string | null;
}): Promise<DispositionResult> {
  return await invoke<DispositionResult>({
    action: 'dispose',
    candidate_organization_id: input.candidateOrganizationId,
    disposition: input.disposition,
    canonical_organization_id: input.canonicalOrganizationId ?? null,
    review_notes: input.reviewNotes ?? null,
  });
}

/**
 * Stranded-case recovery only: the disposition committed but the re-drive
 * never fired. Performs no claim and no disposition write.
 */
export async function resumeRedrive(
  candidateOrganizationId: string,
): Promise<DispositionResult> {
  return await invoke<DispositionResult>({
    action: 'resume',
    candidate_organization_id: candidateOrganizationId,
  });
}
